import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { ThemeProvider, lighten } from '@mui/material/styles'
import { buildFormTheme } from '@cusman/cm-forms-library'
import type { EditorLayout } from '@cusman/cm-forms-library'

interface RestoreSessionDialogProps {
  layout: EditorLayout
  onContinue: () => void
  onStartOver: () => void
}

// Диалог восстановления сохранённых данных формы. Рендерится до FormRender
// (вне его ThemeProvider), поэтому строит тему формы сам через buildFormTheme.
// Primary-цвет темы задаёт иконку, кнопку Continue и лёгкий тинт фона карточки.
export function RestoreSessionDialog({
  layout,
  onContinue,
  onStartOver,
}: RestoreSessionDialogProps) {
  const theme = React.useMemo(() => buildFormTheme(layout), [layout])

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open
        PaperProps={{
          sx: {
            borderRadius: '20px',
            width: '400px',
            maxWidth: 'calc(100vw - 32px)',
            backgroundColor: lighten(theme.palette.primary.main, 0.92),
          },
        }}
      >
        <DialogContent sx={{ px: 4, py: 7, textAlign: 'center' }}>
          <HelpOutlineIcon
            sx={{ color: 'primary.main', fontSize: 52, mb: 2 }}
          />

          {/* Не используем variant="h5": тема формы форсит для заголовков
              мелкие размеры (h5 = 14px), задаём размер явно */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 24,
              color: 'text.primary',
              mb: 1,
            }}
          >
            Restore Form Data
          </Typography>

          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '16px',
              maxWidth: 300,
              mx: 'auto',
              mb: 4,
            }}
          >
            You already have saved form data. Do you want to continue where you
            left off?
          </Typography>

          <Stack direction="row" spacing={1.5} justifyContent="center">
            <Button
              onClick={onStartOver}
              variant="outlined"
              color="inherit"
              disableRipple
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 130,
                px: 3,
                backgroundColor: '#ffffff',
                borderColor: 'grey.300',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: '#ffffff',
                  borderColor: 'grey.400',
                },
              }}
            >
              Start Over
            </Button>
            <Button
              onClick={onContinue}
              variant="contained"
              color="primary"
              autoFocus
              disableRipple
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 130,
                px: 3,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none',
                },
              }}
            >
              Continue
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  )
}
